import {
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Space,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useEffect, useMemo, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import { deleteProjectsService, getAllProjectService } from 'common/services/extends/project';
import { DepartmentItemTypes } from 'common/services/extends/project/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatDateDDMMYYYY } from 'common/utils/functions';

export type TableDataTypes = {
  id: number;
  code?: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  accountant?: DepartmentItemTypes
  operation?: DepartmentItemTypes
  canDelete?: boolean;
};

const TimekeepingManagement: React.FC<ActiveRoles> = ({
  roleIndex,
  roleDelete,
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Selectors */
  const {
    defaultPageSize,
  } = useAppSelector((state) => state.system);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const queryClient = useQueryClient();

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');

  const queryKey = ['project-list-timesheet', keyword, currentPage, currentView];

  /* Queries */
  const { data: listProjectData, isLoading } = useQuery(
    queryKey,
    () => getAllProjectService({
      ...(keyword && { keyword }),
      page: currentPage,
      limit: currentView,
    }),
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['project-delete'],
    async (ids: number[]) => deleteProjectsService({ ids }),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.deleteError'));
      }
    }
  );

  /* Variables */
  const columns: ColumnsType<TableDataTypes> = [
    {
      title: t('system.code'),
      dataIndex: 'code',
      key: 'code',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.code}
        </Typography.Text>
      ),
    },
    {
      title: t('program.name'),
      dataIndex: 'name',
      key: 'name',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.name}
        </Typography.Text>
      ),
    },
    {
      title: t('program.fromDate'),
      dataIndex: 'fromDate',
      key: 'fromDate',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.fromDate, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('program.toDate'),
      dataIndex: 'toDate',
      key: 'toDate',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.toDate, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('program.personInChargeAC'),
      dataIndex: 'accountant',
      key: 'accountant',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.accountant?.fullname}
        </Typography.Text>
      ),
    },
    {
      title: t('program.personInChargeOP'),
      dataIndex: 'operation',
      key: 'operation',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.operation?.fullname}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: string, data: TableDataTypes) => (
        <Space>
          <Tooltip title={t('system.viewDetail')}>
            <Button
              icon={<EyeOutlined />}
              disabled={!roleIndex}
              onClick={() => navigate(`${ROUTE_PATHS.TIMEKEEPING_DETAIL_INFO_MANAGEMENT}?projectId=${data.id}`)}
            />
          </Tooltip>
          <Tooltip title={t('system.delete')}>
            <Button
              disabled={!roleDelete || !data.canDelete}
              icon={<DeleteOutlined />}
              onClick={(e) => {
                if (e.stopPropagation) e.stopPropagation();
                Modal.confirm({
                  className: 't-pagetable_deleteLanguageModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => {
                    if (data.id) {
                      deleteMutate([data.id]);
                    }
                  }
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSetCurrentPage = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('page', page.toString());
    setCurrentPage(page);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const onDelete = async (data: TableDataTypes[], code?: string) => {
    switch (code) {
      case 'allRow':
      case 'all': {
        deleteMutate(data.map((item) => item.id));
        break;
      }
    }
  };

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableData: TableDataTypes[] = useMemo(() => {
    if (!listProjectData?.data) return [];

    return listProjectData.data.map((item) => ({
      id: item.projectData?.id || -1,
      code: item.projectData?.code,
      name: item.projectData?.name,
      fromDate: item.projectData?.fromDate,
      toDate: item.projectData?.toDate,
      accountant: item.accountant,
      operation: item.operation,
      canDelete: item.projectData?.canDelete,
    }));
  }, [listProjectData]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.timeKeeping')}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          handleDelete={onDelete}
          handleSearch={setKeyword}
          isLoading={isLoading || deleteLoading}
          noCheckbox
          tableProps={{
            initShowColumns: ['code', 'name', 'fromDate', 'toDate', 'accountant', 'operation', 'action'],
            columns,
            pageData: tableData || [],
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: listProjectData?.meta.total || 1,
            noBaseCol: true,
            noDeleteLanguage: true,
          }}
        />
      </div>
    </>
  );
};

export default TimekeepingManagement;
